/* eslint-disable react/prop-types */
import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { Card, Typography, Box, Button, IconButton } from '@mui/material';
import { Avatar } from '@mui/material';
import { convertDate, downloadFile } from 'Helpers';
import UserRoleBadge from 'components/shared/UserRoleBadge';

// Reusable Text Truncation Component
const TruncatedText = ({ text, maxLength, maxLines = 3 }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef(null);

    useEffect(() => {
        setIsExpanded(false);
    }, [text]);

    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el || isExpanded) return;
        setIsOverflowing(el.scrollHeight > el.clientHeight + 2);
    }, [text, isExpanded, maxLines]);

    if (!text || text === 'N/A') return <Typography sx={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.6)' }}>N/A</Typography>;

    const shouldTruncateByLength = Boolean(maxLength && text.length > maxLength);
    const displayText = isExpanded || !shouldTruncateByLength ? text : `${text.substring(0, maxLength)}...`;
    const showToggle = shouldTruncateByLength || isOverflowing || isExpanded;

    return (
        <Box>
            <Typography
                ref={textRef}
                sx={{
                    fontSize: '14px',
                    color: 'rgba(0, 0, 0, 0.6)',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    ...(!isExpanded && {
                        display: '-webkit-box',
                        WebkitLineClamp: maxLines,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    })
                }}
            >
                {displayText}
            </Typography>
            {showToggle && (
                <Button
                    onClick={() => setIsExpanded(!isExpanded)}
                    sx={{
                        color: '#1E7C88',
                        fontSize: '12px',
                        textTransform: 'none',
                        padding: '4px 0',
                        minWidth: 'auto',
                        '&:hover': {
                            background: 'transparent',
                            textDecoration: 'underline'
                        }
                    }}
                >
                    {isExpanded ? 'Show less' : 'Show more'}
                </Button>
            )}
        </Box>
    );
};

const ViewProfileModal = ({ setViewProfile, profileData }) => {
    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
                width: '100vw',
                height: '100%',
                position: 'fixed',
                top: 0,
                left: 0,
                zIndex: 1300
            }}
            onClick={() => setViewProfile(false)}
        >
            <Card
                sx={{
                    p: { xs: 2, md: 4 },
                    position: 'relative',
                    mx: { xs: '16px' },
                    maxWidth: { md: '800px', xs: 'auto' },
                    minWidth: { md: '900px', xs: '90vw' },
                    maxHeight: { md: '85vh', xs: '85vh' },
                    overflowY: 'auto',
                    borderRadius: '20px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    background: '#fff',
                    border: '1px solid rgba(30, 124, 136, 0.20)',
                    position: 'relative'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <Box sx={{ position: 'absolute', top: '10px', left: '10px' }}>
                    <UserRoleBadge role={profileData?.role} />
                </Box>
                <Box
                    onClick={() => setViewProfile(false)}
                    sx={{
                        position: 'absolute',
                        right: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        top: '5px',
                        height: '30px',
                        width: '30px',
                        color: 'rgba(0, 0, 0, 0.6)',
                        cursor: 'pointer',
                        '&:hover': {
                            background: 'rgba(0, 0, 0, 0.04)'
                        }
                    }}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </Box>

                <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' }, mt: 2 }}>
                    {/* Left Section - Profile Info */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Avatar
                            src={
                                profileData.profile_image && profileData.profile_image != ''
                                    ? profileData.profile_image
                                    : profileData.first_name
                            }
                            alt={profileData.first_name}
                            sx={{
                                width: '160px',
                                height: '160px',
                                borderRadius: '12px',
                                mb: 3,
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                            }}
                        />

                        <Typography
                            sx={{
                                fontSize: '24px',
                                fontWeight: 600,
                                color: 'rgba(0, 0, 0, 0.87)',
                                mb: 1
                            }}
                        >
                            {profileData.first_name} {profileData.last_name}
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: '16px',
                                color: '#1E7C88',
                                fontWeight: 500,
                                mb: 2
                            }}
                        >
                            {profileData?.job_title ? profileData?.job_title.title : 'N/A'}
                        </Typography>
                        {profileData?.join_date && (
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    color: '#1E7C88',
                                    fontWeight: 500,
                                    mb: 3
                                }}
                            >
                                Joined at {convertDate(profileData.join_date)}
                            </Typography>
                        )}

                        {/* Action Buttons */}
                        <Box display="flex" gap={2} mb={4}>
                            <Button
                                variant="contained"
                                sx={{
                                    background: '#1E7C88',
                                    color: 'white',
                                    borderRadius: '8px',
                                    padding: '8px 20px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        background: '#166d5c',
                                        transform: 'translateY(-1px)'
                                    }
                                }}
                                onClick={() => {
                                    const baseUrl = window.location.origin;
                                    window.open(`${baseUrl}/messenger?chat_type=direct&user_id=${profileData.user_id}`, '_messenger');
                                }}
                                startIcon={
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4Z"
                                            stroke="white"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M22 6L12 13L2 6"
                                            stroke="white"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                }
                            >
                                Send Message
                            </Button>
                            {profileData?.resume && JSON.parse(localStorage.getItem('authUser'))?.role?.role_type === 'admin' && (
                                <Button
                                    variant="outlined"
                                    sx={{
                                        borderColor: '#1E7C88',
                                        color: '#1E7C88',
                                        borderRadius: '8px',
                                        padding: '8px 20px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: '14px',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: '#166d5c',
                                            color: '#166d5c',
                                            background: 'rgba(30, 124, 136, 0.05)',
                                            transform: 'translateY(-1px)'
                                        }
                                    }}
                                    onClick={() => downloadFile(profileData?.resume)}
                                    startIcon={
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <path
                                                d="M7 10L12 15L17 10"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <path
                                                d="M12 15V3"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    }
                                >
                                    Resume
                                </Button>
                            )}
                        </Box>

                        {/* Department Info */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    background: 'rgba(30, 124, 136, 0.08)',
                                    padding: '4px 8px',
                                    borderRadius: '6px'
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 21H21" stroke="#1E7C88" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path
                                        d="M5 21V7L13 3V21"
                                        stroke="#1E7C88"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M19 21V11L13 7"
                                        stroke="#1E7C88"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path d="M9 9V9.01" stroke="#1E7C88" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M9 13V13.01" stroke="#1E7C88" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M9 17V17.01" stroke="#1E7C88" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: '500', color: '#1E7C88' }}>
                                    {profileData.branch?.branch_name || 'N/A'}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    background: 'rgba(30, 124, 136, 0.08)',
                                    padding: '4px 8px',
                                    borderRadius: '6px'
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7Z"
                                        stroke="#1E7C88"
                                        strokeWidth="2"
                                    />
                                    <path d="M7 5V3" stroke="#1E7C88" strokeWidth="2" strokeLinecap="round" />
                                    <path d="M17 5V3" stroke="#1E7C88" strokeWidth="2" strokeLinecap="round" />
                                    <path d="M12 12H12.01" stroke="#1E7C88" strokeWidth="2" strokeLinecap="round" />
                                    <path d="M8 12H8.01" stroke="#1E7C88" strokeWidth="2" strokeLinecap="round" />
                                    <path d="M16 12H16.01" stroke="#1E7C88" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: '500', color: '#1E7C88' }}>
                                    {profileData.department?.name || 'N/A'}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    background: 'rgba(30, 124, 136, 0.08)',
                                    padding: '4px 8px',
                                    borderRadius: '6px'
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M12 2L2 7L12 12L22 7L12 2Z"
                                        stroke="#1E7C88"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M2 17L12 22L22 17"
                                        stroke="#1E7C88"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M2 12L12 17L22 12"
                                        stroke="#1E7C88"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: '500', color: '#1E7C88' }}>
                                    {profileData?.department_divisions && profileData.department_divisions.length > 0
                                        ? profileData.department_divisions[0].name
                                        : 'N/A'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Right Section - Details */}
                    <Box
                        sx={{
                            flex: 1,
                            background: 'rgba(30, 124, 136, 0.02)',
                            borderRadius: '16px',
                            p: 3,
                            border: '1px solid rgba(30, 124, 136, 0.1)'
                        }}
                    >
                        {/* 1. Company ID */}
                        <Box mb={3}>
                            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600, color: '#1E7C88', mb: 1 }}>
                                Company ID
                            </Typography>
                            <Typography sx={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.6)' }}>
                                {profileData.employee_id ?? 'N/A'}
                            </Typography>
                        </Box>

                        {/* 2. Job Role */}
                        <Box mb={3}>
                            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600, color: '#1E7C88', mb: 1 }}>
                                Job Role
                            </Typography>
                            <TruncatedText
                                text={profileData?.role_details ? profileData?.role_details : 'N/A'}
                                maxLength={150}
                                maxLines={3}
                            />
                        </Box>

                        {/* 3. About */}
                        <Box mb={3}>
                            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600, color: '#1E7C88', mb: 1 }}>
                                About
                            </Typography>
                            <TruncatedText text={profileData.about} maxLength={500} maxLines={3} />
                        </Box>

                        {/* 4. Contact Details */}
                        <Box mb={3}>
                            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600, color: '#1E7C88', mb: 1 }}>
                                Contact Details
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                                            stroke="#1E7C88"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <Typography sx={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.6)' }}>
                                        {profileData.phone_no ?? 'N/A'}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                                            stroke="#1E7C88"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M22 6l-10 7L2 6"
                                            stroke="#1E7C88"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <Typography sx={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.6)' }}>
                                        {profileData.email ?? 'N/A'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* 5. Hobby */}
                        <Box mb={3}>
                            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600, color: '#1E7C88', mb: 1 }}>
                                Hobby
                            </Typography>
                            <Typography sx={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.6)' }}>{profileData.hobby ?? 'N/A'}</Typography>
                        </Box>

                        {/* Admin-only sections */}
                        {JSON.parse(localStorage.getItem('authUser'))?.role?.role_type === 'admin' && (
                            <>
                                <Box mb={3}>
                                    <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600, color: '#1E7C88', mb: 1 }}>
                                        Permanent Address
                                    </Typography>
                                    <Typography sx={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.6)' }}>
                                        {profileData?.permanent_address ?? 'N/A'}
                                    </Typography>
                                </Box>
                                <Box mb={3}>
                                    <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600, color: '#1E7C88', mb: 1 }}>
                                        Emergency Contact
                                    </Typography>
                                    <Typography sx={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.6)' }}>
                                        {profileData?.emergency_contact ?? 'N/A'}
                                    </Typography>
                                </Box>
                                <Box mb={3}>
                                    <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600, color: '#1E7C88', mb: 1 }}>
                                        Blood Group
                                    </Typography>
                                    <Typography sx={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.6)' }}>
                                        {profileData?.blood_group ?? 'N/A'}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            </Card>
        </Box>
    );
};

export default ViewProfileModal;
